import React, { useState, useEffect, useCallback } from 'react';
import { followupsAPI, aiAPI } from '../../../services/api';
import { Button, Card, Modal, Paginator } from '../../../components/ui';
import { FOLLOWUP_TYPES, FOLLOWUP_OUTCOMES } from '../../../utils/constants';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

export default function FollowUpsTab({ leadId, lead }) {
  const contacts = lead?.contacts || [];
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [discussModal, setDiscussModal] = useState(null); // followup object being discussed
  const [form, setForm] = useState({ type: 'Call', scheduled_at: '', notes: '', contact_id: '' });
  const [discussForm, setDiscussForm] = useState({ outcome: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Clamp page to valid range when items are added/removed
  useEffect(() => {
    const tp = Math.ceil(followups.length / PAGE_SIZE);
    if (tp > 0 && page > tp) setPage(tp);
  }, [followups.length]);

  const load = useCallback(async () => {
    try {
      const res = await followupsAPI.getByLead(leadId);
      setFollowups(res.data?.data?.followups || []);
    } catch {} finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.scheduled_at) { toast.error('Date/time required'); return; }
    const year = new Date(form.scheduled_at).getFullYear();
    if (year < 2000 || year > 2099) { toast.error('Please enter a valid year (2000–2099)'); return; }
    setSaving(true);
    try {
      const selectedContact = contacts.find(c => c._id === form.contact_id);
      await followupsAPI.create({
        ...form,
        lead_id: leadId,
        contact_id: form.contact_id || null,
        contact_name: selectedContact ? `${selectedContact.name}${selectedContact.designation ? ` (${selectedContact.designation})` : ''}` : null
      });
      toast.success('Follow-up scheduled');
      setModal(false);
      setForm({ type: 'Call', scheduled_at: '', notes: '', contact_id: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule');
    } finally { setSaving(false); }
  };

  const openDiscuss = (f) => {
    setDiscussModal(f);
    setDiscussForm({
      outcome:                f.outcome || '',
      sentiment:              f.sentiment || '',
      interest_level:         f.interest_level || '',
      key_points:             f.key_points || '',
      objections:             f.objections || '',
      next_steps:             f.next_steps || '',
      decision_maker_present: f.decision_maker_present || false,
      notes:                  f.notes || '',
    });
  };

  const saveDiscussion = async () => {
    if (!discussForm.outcome) { toast.error('Select outcome'); return; }
    setSaving(true);
    try {
      const payload = {
        outcome:                discussForm.outcome,
        sentiment:              discussForm.sentiment || null,
        interest_level:         discussForm.interest_level || null,
        key_points:             discussForm.key_points || null,
        objections:             discussForm.objections || null,
        next_steps:             discussForm.next_steps || null,
        decision_maker_present: discussForm.decision_maker_present,
        notes:                  discussForm.notes || null,
      };
      if (discussModal.completed_at) {
        await followupsAPI.update(discussModal._id, payload);
        toast.success('Discussion notes updated');
      } else {
        await followupsAPI.complete(discussModal._id, payload);
        toast.success('Discussion logged');
      }
      setDiscussModal(null);
      setDiscussForm({ outcome: '', sentiment: '', interest_level: '', key_points: '', objections: '', next_steps: '', decision_maker_present: false, notes: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const isOverdue = (f) => !f.completed_at && new Date(f.scheduled_at) < new Date();

  const deleteFollowup = async (f) => {
    if (!window.confirm(`Delete this ${f.type} follow-up?`)) return;
    setDeletingId(f._id);
    try {
      await followupsAPI.delete(f._id);
      setFollowups(fs => fs.filter(x => x._id !== f._id));
      toast.success('Follow-up deleted');
    } catch {
      toast.error('Failed to delete');
    } finally { setDeletingId(null); }
  };

  const getAiSuggestion = async () => {
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const res = await aiAPI.tabAssist({ lead_id: leadId, tab: 'followup' });
      setAiSuggestion(res.data?.data);
    } catch {
      toast.error('AI suggestion failed');
    } finally { setAiLoading(false); }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    const daysOffset = aiSuggestion.suggested_date_offset_days || 1;
    const d = new Date();
    d.setDate(d.getDate() + daysOffset);
    d.setHours(10, 0, 0, 0);
    const iso = d.toISOString().slice(0, 16);
    setForm({
      type: aiSuggestion.type || 'Call',
      scheduled_at: iso,
      notes: aiSuggestion.message_draft || aiSuggestion.talking_points?.join('\n') || '',
      contact_id: ''
    });
    setAiSuggestion(null);
    setModal(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>Follow-ups</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" variant="ghost" onClick={getAiSuggestion} disabled={aiLoading}>
            {aiLoading ? '⏳ Thinking…' : '✨ AI Suggest'}
          </Button>
          <Button size="sm" variant="primary" onClick={() => setModal(true)}>+ Schedule</Button>
        </div>
      </div>

      {/* AI Suggestion Panel */}
      {aiSuggestion && (
        <div style={{ background: '#f0f7ff', border: '1.5px solid #93c5fd', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>✨ AI Recommendation</span>
            <button onClick={() => setAiSuggestion(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>✕</button>
          </div>
          <p style={{ fontSize: 12, color: '#475569', marginBottom: 8, fontStyle: 'italic' }}>{aiSuggestion.reasoning}</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{aiSuggestion.type}</span>
            <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>In {aiSuggestion.suggested_date_offset_days} day(s)</span>
          </div>
          {aiSuggestion.talking_points?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>TALKING POINTS</p>
              {aiSuggestion.talking_points.map((p, i) => (
                <p key={i} style={{ fontSize: 12, color: '#334155', marginBottom: 2 }}>• {p}</p>
              ))}
            </div>
          )}
          {aiSuggestion.message_draft && (
            <div style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>MESSAGE DRAFT</p>
              <p style={{ fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{aiSuggestion.message_draft}</p>
            </div>
          )}
          <Button size="sm" variant="primary" onClick={applyAiSuggestion}>Apply &amp; Schedule →</Button>
        </div>
      )}

      {loading ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</p> : followups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
        <p style={{ fontSize: 28, marginBottom: 8 }}>📋</p>
        <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>No follow-ups scheduled yet</p>
        <p style={{ fontSize: 13, marginTop: 4 }}>Click <strong>+ Schedule</strong> to add a call, WhatsApp, visit or email follow-up.</p>
        <p style={{ fontSize: 12, marginTop: 4, color: '#94a3b8' }}>After scheduling, each follow-up will have a <strong>📝 Log Discussion</strong> button to record what was discussed.</p>
      </div>
      ) : followups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(f => (
        <Card key={f._id} style={{ marginBottom: 10, padding: '14px 16px', border: f.completed_at ? '1px solid #e2e8f0' : isOverdue(f) ? '1.5px solid #fca5a5' : '1.5px solid #bfdbfe' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{f.type}</span>
                {f.completed_at ? (
                  <span style={{ fontSize: 11, background: '#dcfce7', color: '#16a34a', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>✓ Done</span>
                ) : isOverdue(f) ? (
                  <span style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>OVERDUE</span>
                ) : (
                  <span style={{ fontSize: 11, background: '#dbeafe', color: '#2563eb', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>Pending</span>
                )}
                {f.contact_name && (
                  <span style={{ fontSize: 11, background: '#f3e8ff', color: '#7c3aed', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>👤 {f.contact_name}</span>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                📅 {new Date(f.scheduled_at).toLocaleString('en-IN')}
                {f.completed_at && ` · Logged ${new Date(f.completed_at).toLocaleDateString('en-IN')}`}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                {f.outcome && f.outcome !== 'Pending' && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#059669', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: 10 }}>
                    ✓ {f.outcome}
                  </span>
                )}
                {f.sentiment && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: f.sentiment.includes('Positive') ? '#f0fdf4' : f.sentiment === 'Neutral' ? '#fefce8' : '#fef2f2',
                    color:      f.sentiment.includes('Positive') ? '#15803d' : f.sentiment === 'Neutral' ? '#a16207' : '#dc2626',
                    border:     `1px solid ${f.sentiment.includes('Positive') ? '#bbf7d0' : f.sentiment === 'Neutral' ? '#fde68a' : '#fecaca'}` }}>
                    {f.sentiment === 'Very Positive' ? '😄' : f.sentiment === 'Positive' ? '🙂' : f.sentiment === 'Neutral' ? '😐' : f.sentiment === 'Negative' ? '😟' : '😠'} {f.sentiment}
                  </span>
                )}
                {f.interest_level && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: f.interest_level === 'Hot' ? '#fff7ed' : f.interest_level === 'Warm' ? '#fefce8' : '#f8fafc',
                    color:      f.interest_level === 'Hot' ? '#c2410c' : f.interest_level === 'Warm' ? '#a16207' : '#64748b',
                    border:     `1px solid ${f.interest_level === 'Hot' ? '#fed7aa' : f.interest_level === 'Warm' ? '#fde68a' : '#e2e8f0'}` }}>
                    {f.interest_level === 'Hot' ? '🔥' : f.interest_level === 'Warm' ? '🌤️' : f.interest_level === 'Not Interested' ? '❌' : '🧊'} {f.interest_level}
                  </span>
                )}
                {f.decision_maker_present && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#ede9fe', color: '#7c3aed', border: '1px solid #ddd6fe' }}>
                    👔 Decision Maker Present
                  </span>
                )}
              </div>
              {f.key_points && (
                <div style={{ background: '#f0f9ff', borderLeft: '3px solid #38bdf8', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 2 }}>KEY POINTS</p>
                  <p style={{ fontSize: 12, color: '#0c4a6e', whiteSpace: 'pre-wrap' }}>{f.key_points}</p>
                </div>
              )}
              {f.objections && (
                <div style={{ background: '#fff7ed', borderLeft: '3px solid #fb923c', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#c2410c', marginBottom: 2 }}>OBJECTIONS / CONCERNS</p>
                  <p style={{ fontSize: 12, color: '#7c2d12', whiteSpace: 'pre-wrap' }}>{f.objections}</p>
                </div>
              )}
              {f.next_steps && (
                <div style={{ background: '#f0fdf4', borderLeft: '3px solid #4ade80', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 2 }}>NEXT STEPS</p>
                  <p style={{ fontSize: 12, color: '#14532d', whiteSpace: 'pre-wrap' }}>{f.next_steps}</p>
                </div>
              )}
              {f.notes && (
                <div style={{ background: '#f8fafc', borderLeft: '3px solid #cbd5e1', padding: '6px 10px', borderRadius: '0 6px 6px 0', marginTop: 2 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 2 }}>NOTES</p>
                  <p style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>{f.notes}</p>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
              <Button size="sm" variant={f.completed_at ? 'ghost' : 'primary'} onClick={() => openDiscuss(f)}>
                {f.completed_at ? '✏️ Edit Notes' : '📝 Log Discussion'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteFollowup(f)} disabled={deletingId === f._id}
                style={{ fontSize: 11, color: '#ef4444', borderColor: '#fecaca' }}>
                {deletingId === f._id ? '…' : 'Delete'}
              </Button>
            </div>
          </div>
        </Card>
      ))}

      <Paginator page={page} totalPages={Math.ceil(followups.length / PAGE_SIZE)} onPageChange={setPage} />

      {/* Schedule Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Schedule Follow-up">
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={sel}>
                {FOLLOWUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Date & Time *</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} min="2000-01-01T00:00" max="2099-12-31T23:59" style={inp} />
            </div>
          </div>
          {contacts.length > 0 && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Contact Person</label>
              <select value={form.contact_id} onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))} style={sel}>
                <option value="">— Select contact (optional) —</option>
                {contacts.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.name}{c.designation ? ` · ${c.designation}` : ''}{c.department ? ` · ${c.department}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes / Agenda</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, minHeight: 70, resize: 'vertical' }} placeholder="What to discuss, talking points…" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Schedule'}</Button>
          </div>
        </div>
      </Modal>

      {/* Log Discussion Modal */}
      <Modal open={!!discussModal} onClose={() => setDiscussModal(null)}
        title={discussModal?.completed_at ? `Edit Discussion — ${discussModal?.type}` : `Log Discussion — ${discussModal?.type}`}>
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
          📅 Scheduled: {discussModal ? new Date(discussModal.scheduled_at).toLocaleString('en-IN') : ''}
          {discussModal?.contact_name && <> · 👤 {discussModal.contact_name}</>}
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          {/* Row 1: Outcome + Sentiment */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Outcome *</label>
              <select value={discussForm.outcome} onChange={e => setDiscussForm(f => ({ ...f, outcome: e.target.value }))} style={sel}>
                <option value="">Select outcome…</option>
                {FOLLOWUP_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Sentiment / Mood</label>
              <select value={discussForm.sentiment} onChange={e => setDiscussForm(f => ({ ...f, sentiment: e.target.value }))} style={sel}>
                <option value="">Select sentiment…</option>
                {['Very Positive', 'Positive', 'Neutral', 'Negative', 'Very Negative'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Interest Level + Decision Maker */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Interest Level</label>
              <select value={discussForm.interest_level} onChange={e => setDiscussForm(f => ({ ...f, interest_level: e.target.value }))} style={sel}>
                <option value="">Select level…</option>
                {['Hot', 'Warm', 'Cold', 'Not Interested'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569', padding: '8px 0' }}>
                <input type="checkbox" checked={discussForm.decision_maker_present}
                  onChange={e => setDiscussForm(f => ({ ...f, decision_maker_present: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                Decision Maker was present
              </label>
            </div>
          </div>

          {/* Row 3: Key Points Discussed */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Key Points Discussed
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Topics covered, decisions made, what they liked</span>
            </label>
            <textarea value={discussForm.key_points} onChange={e => setDiscussForm(f => ({ ...f, key_points: e.target.value }))}
              style={{ ...inp, minHeight: 72, resize: 'vertical' }}
              placeholder="e.g. Discussed program curriculum. Interested in AIAAI certification. Liked the batch flexibility. Wants to start April batch." />
          </div>

          {/* Row 4: Objections / Concerns */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Objections / Concerns Raised
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— What hesitations or blockers came up</span>
            </label>
            <textarea value={discussForm.objections} onChange={e => setDiscussForm(f => ({ ...f, objections: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. Concerned about fee — wants 10% discount. Wants to confirm student count first. Needs management approval." />
          </div>

          {/* Row 5: Next Steps */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Next Steps Agreed
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— What was committed to / what happens next</span>
            </label>
            <textarea value={discussForm.next_steps} onChange={e => setDiscussForm(f => ({ ...f, next_steps: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. They will share with HODs and revert in 3 days. We will send pricing proposal by tomorrow." />
          </div>

          {/* Row 6: Additional Notes */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
              Additional Notes
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>— Any other context, tone, background</span>
            </label>
            <textarea value={discussForm.notes} onChange={e => setDiscussForm(f => ({ ...f, notes: e.target.value }))}
              style={{ ...inp, minHeight: 60, resize: 'vertical' }}
              placeholder="e.g. Dean Sharma was in a hurry but seemed genuinely interested. She mentioned competitor Simplilearn is also pitching." />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setDiscussModal(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveDiscussion} disabled={saving}>{saving ? 'Saving…' : 'Save Discussion'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
